import { useState, useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { CondosMark } from '../components/brand.js';
import { IconAlert, IconCheck } from '../components/icons.js';
import { Button, cx } from '../components/ui.js';

type InstallMode = 'prompt' | 'ios' | null;
type ConnectionNotice = 'offline' | 'restored' | null;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface StandaloneNavigator extends Navigator {
  standalone?: boolean;
}

interface PwaSnapshot {
  isOnline: boolean;
  isInstalled: boolean;
  installMode: InstallMode;
  connectionNotice: ConnectionNotice;
  offlineReady: boolean;
  needRefresh: boolean;
  updateSnoozed: boolean;
  updateBlocked: boolean;
  updateInProgress: boolean;
}

const INSTALL_DISMISS_KEY = 'condos:pwa-install-dismissed:v1';
const INSTALL_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;
const UPDATE_SNOOZE_MS = 30 * 60 * 1000;
const UPDATE_CHECK_MS = 60 * 60 * 1000;

let pendingInstallPrompt: BeforeInstallPromptEvent | null = null;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | null = null;
let connectionTimer: number | undefined;
let offlineReadyTimer: number | undefined;
let updateSnoozeTimer: number | undefined;
let updateAuthorized = false;
let workerActivated = false;
let reloadRequested = false;

const dirtyForms = new WeakSet<HTMLFormElement>();
const listeners = new Set<() => void>();

function isIosDevice(): boolean {
  const navigatorWithTouch = navigator as Navigator & { maxTouchPoints?: number };
  return (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigatorWithTouch.maxTouchPoints ?? 0) > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as StandaloneNavigator).standalone === true
  );
}

function isInstallDismissed(): boolean {
  try {
    const dismissedUntil = Number(localStorage.getItem(INSTALL_DISMISS_KEY));
    if (!Number.isFinite(dismissedUntil) || dismissedUntil <= Date.now()) {
      localStorage.removeItem(INSTALL_DISMISS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function initialInstallMode(installed: boolean): InstallMode {
  if (installed || isInstallDismissed()) return null;
  return isIosDevice() ? 'ios' : null;
}

const initiallyInstalled = isStandalone();
let snapshot: PwaSnapshot = {
  isOnline: navigator.onLine,
  isInstalled: initiallyInstalled,
  installMode: initialInstallMode(initiallyInstalled),
  connectionNotice: navigator.onLine ? null : 'offline',
  offlineReady: false,
  needRefresh: false,
  updateSnoozed: false,
  updateBlocked: false,
  updateInProgress: false,
};

function setSnapshot(patch: Partial<PwaSnapshot>): void {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): PwaSnapshot {
  return snapshot;
}

export function usePwa(): PwaSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function dismissInstall(): void {
  pendingInstallPrompt = null;
  try {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now() + INSTALL_DISMISS_MS));
  } catch {
    // O modo privado pode bloquear storage. A sessao atual ainda respeita a dispensa.
  }
  setSnapshot({ installMode: null });
}

async function requestInstall(): Promise<void> {
  const prompt = pendingInstallPrompt;
  if (!prompt || snapshot.installMode !== 'prompt') return;

  pendingInstallPrompt = null;
  setSnapshot({ installMode: null });
  await prompt.prompt();
  const choice = await prompt.userChoice;
  if (choice.outcome === 'dismissed') dismissInstall();
}

function isControlDirty(control: Element): boolean {
  if (control instanceof HTMLInputElement) {
    if (['button', 'hidden', 'image', 'reset', 'submit'].includes(control.type)) return false;
    if (control.type === 'checkbox' || control.type === 'radio') {
      return control.checked !== control.defaultChecked;
    }
    return control.value !== control.defaultValue;
  }

  if (control instanceof HTMLTextAreaElement) return control.value !== control.defaultValue;
  if (control instanceof HTMLSelectElement) {
    return Array.from(control.options).some((option) => option.selected !== option.defaultSelected);
  }

  return false;
}

function hasPotentiallyUnsavedFormData(): boolean {
  return Array.from(document.forms).some(
    (form) => dirtyForms.has(form) || Array.from(form.elements).some(isControlDirty),
  );
}

function markWorkerActiveAndMaybeReload(): void {
  workerActivated = true;
  if (!updateAuthorized || reloadRequested) return;

  if (hasPotentiallyUnsavedFormData()) {
    setSnapshot({
      needRefresh: true,
      updateBlocked: true,
      updateInProgress: false,
      updateSnoozed: false,
    });
    return;
  }

  reloadRequested = true;
  window.location.reload();
}

async function requestUpdate(): Promise<void> {
  if (!snapshot.needRefresh || !updateServiceWorker) return;

  if (hasPotentiallyUnsavedFormData()) {
    setSnapshot({ updateBlocked: true, updateInProgress: false, updateSnoozed: false });
    return;
  }

  setSnapshot({ updateBlocked: false, updateInProgress: true, updateSnoozed: false });
  updateAuthorized = true;

  if (workerActivated) {
    markWorkerActiveAndMaybeReload();
    return;
  }

  try {
    await updateServiceWorker(false);
  } catch {
    updateAuthorized = false;
    setSnapshot({ updateInProgress: false });
  }
}

function snoozeUpdate(): void {
  window.clearTimeout(updateSnoozeTimer);
  setSnapshot({ updateSnoozed: true, updateBlocked: false });
  updateSnoozeTimer = window.setTimeout(() => {
    setSnapshot({ updateSnoozed: false });
  }, UPDATE_SNOOZE_MS);
}

function checkForUpdate(): void {
  if (!serviceWorkerRegistration || !navigator.onLine || document.visibilityState !== 'visible') {
    return;
  }
  void serviceWorkerRegistration.update().catch(() => undefined);
}

function initializePwa(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    const installEvent = event as BeforeInstallPromptEvent;
    installEvent.preventDefault();
    pendingInstallPrompt = installEvent;

    if (!snapshot.isInstalled && !isInstallDismissed()) {
      setSnapshot({ installMode: 'prompt' });
    }
  });

  window.addEventListener('appinstalled', () => {
    pendingInstallPrompt = null;
    try {
      localStorage.removeItem(INSTALL_DISMISS_KEY);
    } catch {
      // A instalacao esta concluida mesmo se o storage estiver indisponivel.
    }
    setSnapshot({ isInstalled: true, installMode: null });
  });

  const displayMode = window.matchMedia('(display-mode: standalone)');
  displayMode.addEventListener('change', () => {
    const installed = isStandalone();
    setSnapshot({
      isInstalled: installed,
      installMode: installed ? null : initialInstallMode(false),
    });
  });

  window.addEventListener('offline', () => {
    window.clearTimeout(connectionTimer);
    setSnapshot({ isOnline: false, connectionNotice: 'offline' });
  });

  window.addEventListener('online', () => {
    window.clearTimeout(connectionTimer);
    setSnapshot({ isOnline: true, connectionNotice: 'restored' });
    connectionTimer = window.setTimeout(() => {
      setSnapshot({ connectionNotice: null });
    }, 5_000);
    checkForUpdate();
  });

  const markDirty = (event: Event) => {
    if (!(event.target instanceof Element)) return;
    const form = event.target.closest('form');
    if (form instanceof HTMLFormElement) dirtyForms.add(form);
  };
  document.addEventListener('input', markDirty, true);
  document.addEventListener('change', markDirty, true);
  // Nao limpamos no evento `reset`: em formularios controlados pelo React, o
  // DOM pode disparar reset e receber o valor nao salvo de volta logo depois.
  // Quando o fluxo termina, a tela/modal desmonta o form e ele deixa de entrar
  // em `document.forms`, liberando a atualizacao sem arriscar dados digitados.

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', markWorkerActiveAndMaybeReload);
  }

  document.addEventListener('visibilitychange', checkForUpdate);
  window.setInterval(checkForUpdate, UPDATE_CHECK_MS);

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh: () => {
      updateAuthorized = false;
      workerActivated = false;
      window.clearTimeout(updateSnoozeTimer);
      setSnapshot({
        needRefresh: true,
        updateSnoozed: false,
        updateBlocked: false,
        updateInProgress: false,
      });
    },
    onNeedReload: markWorkerActiveAndMaybeReload,
    onOfflineReady: () => {
      window.clearTimeout(offlineReadyTimer);
      setSnapshot({ offlineReady: true });
      offlineReadyTimer = window.setTimeout(() => setSnapshot({ offlineReady: false }), 5_000);
    },
    onRegisteredSW: (_scriptUrl, registration) => {
      serviceWorkerRegistration = registration ?? null;
    },
    onRegisterError: () => {
      // A aplicacao continua online normalmente; o erro sera detectado na validacao do build.
    },
  });
}

initializePwa();

export function InstallAppButton({ className }: { className?: string }) {
  const { installMode } = usePwa();
  const [showIosHelp, setShowIosHelp] = useState(false);

  if (!installMode) return null;

  const isIos = installMode === 'ios';

  return (
    <section className={cx('rounded-xl border border-line bg-surface p-2', className)}>
      <button
        type="button"
        onClick={() => {
          if (isIos) setShowIosHelp((visible) => !visible);
          else void requestInstall();
        }}
        aria-expanded={isIos ? showIosHelp : undefined}
        className="flex min-h-11 w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm font-bold text-fg transition hover:bg-surface-2"
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-accent text-on-accent">
          <CondosMark className="h-4.5 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block">Instalar aplicativo</span>
          <span className="block text-xs font-medium text-muted">
            {isIos ? 'Adicionar à Tela de Início' : 'Abrir o Condos como app'}
          </span>
        </span>
      </button>

      {isIos && showIosHelp ? (
        <div className="px-2.5 pb-2 pt-2 text-xs leading-relaxed text-muted">
          <p className="font-semibold text-fg">No iPhone, use o Safari:</p>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            <li>Toque em Compartilhar.</li>
            <li>Escolha “Adicionar à Tela de Início”.</li>
            <li>Confirme em “Adicionar”.</li>
          </ol>
          <button
            type="button"
            onClick={dismissInstall}
            className="mt-2 min-h-11 rounded-lg px-2 font-bold text-muted transition hover:bg-surface-2 hover:text-fg"
          >
            Não mostrar novamente
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={dismissInstall}
          className="min-h-11 rounded-lg px-2.5 text-xs font-bold text-muted transition hover:bg-surface-2 hover:text-fg"
        >
          Agora não
        </button>
      )}
    </section>
  );
}

export function PwaExperience() {
  const {
    connectionNotice,
    offlineReady,
    needRefresh,
    updateSnoozed,
    updateBlocked,
    updateInProgress,
  } = usePwa();

  const showUpdate = needRefresh && !updateSnoozed;
  const showConnection = connectionNotice !== null;
  const showOfflineReady = offlineReady && !showConnection && !showUpdate;

  if (!showUpdate && !showConnection && !showOfflineReady) return null;

  return (
    <div className="pwa-notification-stack pointer-events-none fixed z-50 flex w-auto flex-col items-end gap-3">
      {showUpdate ? (
        <section
          role="region"
          aria-live="polite"
          aria-labelledby="pwa-update-title"
          className="pointer-events-auto w-full max-w-sm rounded-card border border-line bg-surface p-4 shadow-2xl shadow-ink/70"
        >
          <div className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent">
              <CondosMark className="h-4.5 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p id="pwa-update-title" className="font-bold">
                Nova versão disponível
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted">
                {updateBlocked
                  ? 'Conclua ou salve o formulário aberto antes de atualizar.'
                  : 'Atualize quando puder. O Condos nunca recarrega sozinho.'}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="quiet" onClick={snoozeUpdate}>
              Depois
            </Button>
            <Button type="button" loading={updateInProgress} onClick={() => void requestUpdate()}>
              {updateBlocked ? 'Verificar novamente' : 'Atualizar agora'}
            </Button>
          </div>
        </section>
      ) : null}

      {showConnection ? (
        <div
          role="status"
          aria-live="polite"
          className={cx(
            'pointer-events-auto flex max-w-sm items-center gap-2 rounded-pill border bg-surface px-4 py-3 text-sm font-semibold shadow-xl shadow-ink/60',
            connectionNotice === 'offline'
              ? 'border-warn/40 text-warn'
              : 'border-accent/40 text-accent',
          )}
        >
          {connectionNotice === 'offline' ? (
            <IconAlert className="size-4.5 shrink-0" />
          ) : (
            <IconCheck className="size-4.5 shrink-0" />
          )}
          <span>
            {connectionNotice === 'offline'
              ? 'Sem conexão. Os dados não serão atualizados.'
              : 'Conexão restabelecida.'}
          </span>
        </div>
      ) : null}

      {showOfflineReady ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto flex max-w-sm items-center gap-2 rounded-pill border border-accent/40 bg-surface px-4 py-3 text-sm font-semibold text-accent shadow-xl shadow-ink/60"
        >
          <IconCheck className="size-4.5 shrink-0" />
          <span>Proteção offline preparada.</span>
        </div>
      ) : null}
    </div>
  );
}
